/**
 * Defensively stringify any value that came back from a backend error path.
 *
 * Why: FastAPI / Pydantic v2 validation errors return:
 *   { detail: [ { type, loc, msg, input, ctx, url }, … ] }
 *
 * If you pass that array (or a single such object) into a React child or
 * Sonner `description`, React throws:
 *   "Objects are not valid as a React child (found: object with keys
 *    {type, loc, msg, input, ctx, url})"
 *
 * That overlay covers the entire app — even when the actual operation
 * succeeded server-side. This helper turns ANY shape into a safe string:
 *   - string  → returned as-is
 *   - Pydantic v2 single object → "msg (at loc.path)"
 *   - Pydantic v2 array → joined "msg (at loc.path); …"
 *   - any other object → JSON.stringify, capped at 240 chars
 *   - undefined/null → fallback message
 */
export function safeErrorText(value, fallback = "Unknown error") {
  if (value == null) return fallback;
  if (typeof value === "string") return value;

  // Pydantic v2 array of error objects
  if (Array.isArray(value)) {
    return value
      .map((v) => safeErrorText(v))
      .filter(Boolean)
      .join("; ")
      .slice(0, 600) || fallback;
  }

  // Single Pydantic-shaped object?
  if (typeof value === "object") {
    if (typeof value.msg === "string") {
      const loc = Array.isArray(value.loc) ? value.loc.join(".") : "";
      return loc ? `${value.msg} (at ${loc})` : value.msg;
    }
    if (typeof value.detail === "string") return value.detail;
    if (typeof value.message === "string") return value.message;
    // Last resort — stringify (capped).
    try {
      return JSON.stringify(value).slice(0, 240);
    } catch {
      return fallback;
    }
  }

  // Numbers, booleans, etc.
  return String(value);
}

/** Convenience: extract a safe display string from an axios-style error.
 *  Tries `e.response.data.detail`, then `e.response.data`, then `e.message`. */
export function safeApiError(e, fallback = "Request failed") {
  if (!e) return fallback;
  const data = e?.response?.data;
  if (data != null) {
    if (typeof data === "string") return data.slice(0, 600);
    if (data.detail != null) return safeErrorText(data.detail, fallback);
    return safeErrorText(data, fallback);
  }
  return safeErrorText(e?.message || e, fallback);
}
